import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import DomainLogo from "./DomainLogo";

const LANGUAGES = [
  { code: "en", label: "EN", flag: "\u{1F1FA}\u{1F1F8}" },
  { code: "es", label: "ES", flag: "\u{1F1EA}\u{1F1F8}" },
];

const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);

  const langRef = useRef<HTMLDivElement>(null);
  const acctRef = useRef<HTMLDivElement>(null);

  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const changeLang = (lng: string) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
    setMenuOpen(false);
  };

  const handleSignOut = () => {
    setAcctOpen(false);
    setMenuOpen(false);
    logout();
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
      if (acctRef.current && !acctRef.current.contains(e.target as Node)) {
        setAcctOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const currentLang =
    LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  return (
    <nav className="sticky top-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur-sm">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="shrink-0">
          <DomainLogo
            type="header"
            alt={t("logoAlt")}
            style={{ height: 28 }}
            fallbackSrc="https://mylighthouse.co/assets/images/lighthouse.png"
          />
        </Link>

        {/* Mobile menu button */}
        <button
          className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 lg:hidden"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {/* Desktop nav */}
        <div className="hidden items-center gap-1 lg:flex">
          <Link
            to="/"
            className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-primary"
          >
            {t("nav.home")}
          </Link>

          {user ? (
            <div className="relative" ref={acctRef}>
              <button
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-primary"
                onClick={() => setAcctOpen((o) => !o)}
              >
                {t("nav.account")}
                <svg className={`h-4 w-4 transition-transform ${acctOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {acctOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
                  <Link
                    className="block px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                    to="/profile"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t("nav.myProfile")}
                  </Link>
                  <Link
                    className="block px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                    to="/payment-methods"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t("nav.paymentMethods")}
                  </Link>
                  <button
                    className="block w-full px-4 py-2.5 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
                    onClick={handleSignOut}
                  >
                    {t("nav.signOut")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-primary"
            >
              {t("nav.signIn")}
            </Link>
          )}

          <div className="mx-1 h-5 w-px bg-gray-200" />

          <div className="relative" ref={langRef}>
            <button
              className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
              onClick={() => setLangOpen((o) => !o)}
            >
              {currentLang.flag} {currentLang.label}
              <svg className={`h-4 w-4 transition-transform ${langOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {langOpen && (
              <div className="absolute right-0 top-full mt-1 w-32 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    className="block w-full px-4 py-2.5 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                    onClick={(e) => {
                      e.preventDefault();
                      changeLang(l.code);
                    }}
                  >
                    {l.flag} {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      {menuOpen && (
        <div className="border-t border-gray-100 bg-white px-4 pb-4 pt-2 lg:hidden">
          <Link
            to="/"
            className="block rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            onClick={() => setMenuOpen(false)}
          >
            {t("nav.home")}
          </Link>

          {user ? (
            <>
              <Link
                to="/profile"
                className="block rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setMenuOpen(false)}
              >
                {t("nav.myProfile")}
              </Link>
              <Link
                to="/payment-methods"
                className="block rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setMenuOpen(false)}
              >
                {t("nav.paymentMethods")}
              </Link>
              <button
                className="block w-full rounded-lg px-3 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
                onClick={handleSignOut}
              >
                {t("nav.signOut")}
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="block rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => setMenuOpen(false)}
            >
              {t("nav.signIn")}
            </Link>
          )}

          <div className="my-2 border-t border-gray-100" />

          <div className="flex gap-2 px-3">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  l.code === currentLang.code
                    ? "bg-primary/10 text-primary"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
                onClick={() => changeLang(l.code)}
              >
                {l.flag} {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Header;
